import React, { useState } from 'react';
import { getAge, formatDate } from '../ContratFunctions';
import Papa from 'papaparse';
import { FaUserFriends, FaPlus, FaFileImport, FaDownload } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import CSVConversionModal from './CSVModal';
import { isValidUTF8 } from '../ContratFunctions';
import { sanitizeInput } from '../ContratFunctions';

const Step2 = ({ handleStepChange, beneficiaire, setBeneficiaire, showErrorFormulaire, dateRetour }) => {
    const [currentBeneficiaire, setCurrentBeneficiaire] = useState({ nom: '', prenom: '', dateNaissance: '' });
    const [showModal, setShowModal] = useState(false);

    const handleUserChange = (e) => {
        const { name, value } = e.target;
        setCurrentBeneficiaire({
            ...currentBeneficiaire,
            [name]: sanitizeInput(value), // Sanitisation des entrées
        });
    };

    const handleDateChange = (e) => {
        let value = sanitizeInput(e.target.value); // Sanitisation
        const [year, month, day] = value.split('-');

        if (year.length > 4) {
            value = `${year.slice(0, 4)}-${month}-${day}`;
        }

        e.target.value = value;
        handleUserChange(e);
    };

    const addBeneficiaire = () => {
        const sanitizedNom = sanitizeInput(currentBeneficiaire.nom);
        const sanitizedPrenom = sanitizeInput(currentBeneficiaire.prenom);
        const sanitizedDateNaissance = sanitizeInput(currentBeneficiaire.dateNaissance);

        if (!sanitizedNom || !sanitizedPrenom || !sanitizedDateNaissance) {
            showErrorFormulaire("Veuillez remplir tous les champs.");
            return;
        }

        const age = getAge(sanitizedDateNaissance);
        if (age >= 150 || age < 0) {
            Swal.fire(
                'Erreur : la date de naissance est incorrecte !',
                'Le bénéficiaire ne peut être âgé de plus de 150 ans ou doit être né après aujourd’hui.',
                'error'
            );
            return;
        }

        setBeneficiaire([...beneficiaire, { nom: sanitizedNom, prenom: sanitizedPrenom, dateNaissance: sanitizedDateNaissance, id: Date.now() }]);
        setCurrentBeneficiaire({ nom: '', prenom: '', dateNaissance: '' });
    };

    const removeBeneficiaire = (id) => {
        setBeneficiaire(beneficiaire.filter(user => user.id !== id));
    };

    const handleOnClick = (beneficiaire) => {
        if (beneficiaire.length <= 0) {
            toast.error("Merci d'ajouter au moins une personne.");
            return;
        }
        handleStepChange(3);
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const fileExtension = file.name.split('.').pop().toLowerCase();
        if (fileExtension !== 'csv') {
            toast.error("Format de fichier non supporté. Veuillez importer un fichier CSV.");
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            const arrayBuffer = event.target.result;
            const uint8Array = new Uint8Array(arrayBuffer);

            if (isValidUTF8(uint8Array)) {
                parseCSV(file); // Fichier valide, on continue
            } else {
                toast.error("Le fichier doit être encodé en UTF-8.");
                setShowModal(true);
            }

            e.target.value = ''; // Réinitialisation de l'input
        };

        reader.onerror = () => {
            toast.error("Erreur lors de la lecture du fichier.");
        };

        reader.readAsArrayBuffer(file);
    };

    const handleDlCSVExample = () => {
        const fileUrl = `${process.env.PUBLIC_URL}/exemple.csv`;
        const link = document.createElement('a');
        link.href = fileUrl;
        link.setAttribute('download', 'exemple.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const parseCSV = (file) => {
        Papa.parse(file, {
            complete: (result) => {
                try {
                    processImportedData(result.data);
                } catch (error) {
                    toast.error(`Erreur lors de l'importation des données : ${error.message}`);
                }
            },
            header: true,
            skipEmptyLines: true,
        });
    };

    const processImportedData = (data) => {
        const newBeneficiaires = data.map((row, index) => {
            const sanitizedNom = sanitizeInput(row.nom);
            const sanitizedPrenom = sanitizeInput(row.prenom);
            const rawDateNaissance = row['date de naissance'];
    
            if (!sanitizedNom || !sanitizedPrenom || !rawDateNaissance) {
                toast.warning(`Ligne ${index + 1} ignorée : Nom, prénom, et date de naissance sont obligatoires.`);
                return null;
            }
    
            // Formater la date avant de la sanitiser
            const formattedDate = localFormatDate(rawDateNaissance);
            if (!formattedDate) {
                toast.warning(`Ligne ${index + 1} ignorée : Date de naissance non valide.`);
                return null;
            }
    
            // Sanitiser la date formatée uniquement après validation
            const sanitizedDateNaissance = sanitizeInput(formattedDate);
    
            return {
                nom: sanitizedNom,
                prenom: sanitizedPrenom,
                dateNaissance: sanitizedDateNaissance,
                id: Date.now() + Math.random(),
            };
        }).filter(Boolean);
    
        setBeneficiaire(prev => [...prev, ...newBeneficiaires]);
    
        if (newBeneficiaires.length > 0) {
            toast.success(`${newBeneficiaires.length} bénéficiaires ajoutés.`);
        } else {
            toast.warning("Aucun bénéficiaire ajouté : Toutes les lignes contenaient des erreurs.");
        }
    };
    

    const localFormatDate = (date) => {
        if (!date) return '';

        try {
            if (typeof date === 'string') {
                const dateParts = date.split(/[/-]/);
                if (dateParts.length === 3) {
                    const [day, month, year] = dateParts;
                    const parsedDate = new Date(`${year}-${month}-${day}`);
                    if (parsedDate && !isNaN(parsedDate)) {
                        return `${year}-${month}-${day}`;
                    } else {
                        throw new Error(`Format de date non valide : ${date}`);
                    }
                }
            }

            if (date instanceof Date && !isNaN(date)) {
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                return `${year}-${month}-${day}`;
            }
        } catch (error) {
            console.error("Erreur de formatage de date :", error);
        }

        return '';
    };

    return (
        <div className="p-6 bg-white rounded-lg shadow-md">
            <h2 className="mb-4 text-xl font-bold text-axa-blue-400">Personnes Affectées au Contrat</h2>
            <div className="grid grid-cols-1 gap-4 mb-4 md:grid-cols-3">
                <div className="relative flex w-full h-12 rounded-xl">
                    <input
                        className="w-full px-4 text-base bg-transparent bg-white border border-gray-200 outline-none peer rounded-xl focus:ring-axa-blue-400 focus:border-axa-blue-400"
                        id="nom"
                        name="nom"
                        type="text"
                        value={currentBeneficiaire.nom}
                        onChange={handleUserChange}
                        required
                    />
                    <label className="absolute text-gray-700 top-1/2 translate-y-[-50%] bg-white left-4 px-2 peer-focus:top-0 peer-focus:left-3 font-semibold text-base peer-focus:text-sm peer-focus:text-axa-blue-400 peer-valid:-top-0 peer-valid:left-3 peer-valid:text-sm peer-valid:text-axa-blue-400 duration-150" htmlFor="nom">
                        Nom*
                    </label>
                </div>
                <div className="relative flex w-full h-12 rounded-xl">
                    <input
                        className="w-full px-4 text-base bg-transparent bg-white border border-gray-200 outline-none peer rounded-xl focus:ring-axa-blue-400 focus:border-axa-blue-400"
                        id="prenom"
                        name="prenom"
                        type="text"
                        value={currentBeneficiaire.prenom}
                        onChange={handleUserChange}
                        required
                    />
                    <label className="absolute text-gray-700 top-1/2 translate-y-[-50%] bg-white left-4 px-2 peer-focus:top-0 peer-focus:left-3 font-semibold text-base peer-focus:text-sm peer-focus:text-axa-blue-400 peer-valid:-top-0 peer-valid:left-3 peer-valid:text-sm peer-valid:text-axa-blue-400 duration-150" htmlFor="prenom">
                        Prénom*
                    </label>
                </div>
                <div className="relative flex w-full h-12 rounded-xl">
                    <input
                        className="w-full px-4 text-base bg-transparent bg-white border border-gray-200 outline-none peer rounded-xl focus:ring-axa-blue-400 focus:border-axa-blue-400"
                        id="dateNaissance"
                        name="dateNaissance"
                        type="date"
                        value={currentBeneficiaire.dateNaissance}
                        onChange={handleDateChange}
                        required
                    />
                    <label className="absolute text-gray-700 top-1/2 translate-y-[-50%] bg-white left-4 px-2 peer-focus:top-0 peer-focus:left-3 font-semibold text-base peer-focus:text-sm peer-focus:text-axa-blue-400 peer-valid:-top-0 peer-valid:left-3 peer-valid:text-sm peer-valid:text-axa-blue-400 duration-150" htmlFor="dateNaissance">
                        Date de Naissance*
                    </label>
                </div>
                <CSVConversionModal show={showModal} setShow={setShowModal} />
            </div>

            <div className="flex w-full mb-4">
                <div className="flex items-center mt-2 space-x-4">
                    <button
                        onClick={addBeneficiaire}
                        className="flex items-center w-auto px-4 py-2 mt-4 font-semibold text-white transition duration-300 rounded bg-success-green hover:bg-green-800 md:mt-0"
                    >
                        <FaPlus className="w-4 h-4 mr-2" />
                        Ajouter
                    </button>
                </div>
                <div className="flex items-start mt-2 space-x-4">
                    <input type="file" accept=".csv" onChange={handleFileUpload} id="file-upload" className="hidden" />
                    <label htmlFor="file-upload" className="flex items-center justify-center w-48 px-4 py-2 font-semibold text-white rounded shadow-lg cursor-pointer bg-axa-blue-400 hover:bg-hover-axa-blue">
                        <FaFileImport className="w-4 h-4 mr-2" />
                        Importer (CSV)
                    </label>
                </div>
                <div className="flex items-center mt-2 ml-4 space-x-4">
                    <button
                        onClick={handleDlCSVExample}
                        className="flex items-center w-auto px-4 py-2 mt-4 font-semibold text-white transition duration-300 rounded bg-warning-yellow hover:bg-yellow-600 md:mt-0"
                    >
                        <FaDownload className="w-4 h-4 mr-2" />
                        Télécharger le modèle CSV
                    </button>
                </div>
            </div>

            <div className="p-4 bg-gray-100 border-l-4 border-gray-400 rounded-lg shadow-lg">
                <div className="flex items-center mb-4">
                    <div className="p-3 mr-4 bg-gray-500 rounded-full">
                        <FaUserFriends className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">Liste des Bénéficiaires</h3>
                </div>
                {beneficiaire.length > 0 ? (
                    <div className="space-y-4">
                        {beneficiaire.map((user) => (
                            <div key={user.id} className="flex items-center justify-between p-4 transition duration-200 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50">
                                <div>
                                    <p className="text-lg font-semibold text-gray-800">{user.nom} {user.prenom}</p>
                                    <p className="text-sm text-gray-600">Date de naissance : {formatDate(user.dateNaissance)}</p>
                                </div>
                                <button onClick={() => removeBeneficiaire(user.id)} className="transition duration-300 text-danger-red hover:text-red-700">
                                    Supprimer
                                </button>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-center text-gray-600">Aucun utilisateur ajouté.</p>
                )}
            </div>

            <div className="flex justify-between mt-6 space-x-4">
                <button
                    type="button"
                    onClick={() => handleStepChange(1)}
                    className="px-4 py-2 text-white transition duration-300 bg-gray-500 rounded hover:bg-gray-700"
                >
                    Précédent
                </button>
                <button
                    type="button"
                    onClick={() => handleOnClick(beneficiaire)}
                    className="px-4 py-2 text-white transition duration-300 rounded bg-axa-blue-400 hover:bg-hover-axa-blue hover:bg-axa-blue-700"
                >
                    Suivant
                </button>
            </div>
        </div>
    );
};

export default Step2;
